import {
  AddressCard,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  ModalV2,
  SkeletonLoader,
  ToastMessage,
} from '@equip.health/ui';

import {
  OrderEquipScaleFrom,
  OrderSteps,
} from '~/lib/constants/patients.constants';
import useOrderEquipScale from '~/lib/hooks/useOrderEquipScale';
import { formatCellPhoneNumber, getAgeFromDOB } from '~/lib/util/patient.util';

import CustomShippingAddress from './CustomShippingAddress';
import { PROCEED_WITH_ORDER_EVENT_BY_WARNING } from './equipScale.constants';
import ShippingAddress from './ShippingAddress';
import WarningContent from '~/components/maud/patients/profile/clinical-profile/equip-scale/WarningContent';
import { PatientClinicalEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface OrderEquipScaleProps {
  onClose: () => void;
  openOrderEquipScaleFrom: OrderEquipScaleFrom;
  patient: Patient;
  isReorder?: boolean;
  reorderReasonCode?: string;
}

const TOP_LEVEL_ID = 'order-equip-scale';

const OrderEquipScale = ({
  onClose,
  openOrderEquipScaleFrom,
  patient,
  isReorder,
  reorderReasonCode,
}: OrderEquipScaleProps) => {
  const patientId = patient.userId;
  const age = patient?.dateOfBirth
    ? getAgeFromDOB(patient?.dateOfBirth, 'yyyy-LL-dd').toString()
    : '';

  const {
    addressData,
    warnings,
    currentWarning,
    currentStep,
    modalTitle,
    selectedAddress,
    handleProceedWithOrder,
    handleAddressSelection,
    setCurrentStep,
    handleConfirmOrder,
    orderBodyTraceError,
    orderBodyTraceLoading,
  } = useOrderEquipScale(
    patientId,
    age,
    patient,
    onClose,
    isReorder,
    reorderReasonCode,
  );

  const { trackEvent } = useAnalytics();

  const WarningStep = ({
    warnings,
    currentWarning,
    handleProceedWithOrder,
  }) => {
    const handleProceedClick = () => {
      const warningType = warnings[currentWarning].type;
      const event =
        openOrderEquipScaleFrom === OrderEquipScaleFrom.NONE
          ? undefined
          : PROCEED_WITH_ORDER_EVENT_BY_WARNING[openOrderEquipScaleFrom][
              warningType
            ];
      if (event) {
        trackEvent(event, {
          source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
        });
      }
      handleProceedWithOrder();
    };
    return (
      <ChakraFlex
        direction="column"
        gap="24px"
        id={`${TOP_LEVEL_ID}__warning-step`}
      >
        <WarningContent warning={warnings[currentWarning]?.type} />
        <FireflyButton
          id={`${TOP_LEVEL_ID}__proceed-with-order-button`}
          onClick={handleProceedClick}
        >
          Proceed with order
        </FireflyButton>
      </ChakraFlex>
    );
  };

  const ShippingAddressStep = ({ AddressData, handleAddressSelection }) => (
    <ChakraFlex
      direction="column"
      gap="24px"
      id={`${TOP_LEVEL_ID}__shipping-address-step`}
    >
      <ShippingAddress
        addresses={AddressData}
        onSelectAddress={(address) => {
          trackEvent(
            PatientClinicalEvents[
              `${openOrderEquipScaleFrom}ShippingAddressConfirmationModalUseThisAddressClicked`
            ],
            {
              source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
            },
          );
          handleAddressSelection(address);
        }}
      />
      <FireflyButton
        id={`${TOP_LEVEL_ID}__enter-address-manually-button`}
        leftIcon={FAIconName.penRegular}
        onClick={() => {
          trackEvent(
            PatientClinicalEvents[
              `${openOrderEquipScaleFrom}ShippingAddressConfirmationModalEnterAddressManuallyClicked`
            ],
            {
              source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
            },
          );
          setCurrentStep(OrderSteps.ENTER_MANUAL_ADDRESS);
        }}
        variant="secondary"
      >
        Enter Address Manually
      </FireflyButton>
    </ChakraFlex>
  );
  const EnterManualAddressStep = ({
    addressData,
    setCurrentStep,
    handleAddressSelection,
  }) => {
    return (
      <ChakraFlex
        direction="column"
        gap="24px"
        id={`${TOP_LEVEL_ID}__manual-address-step`}
      >
        <CustomShippingAddress
          addressData={addressData}
          onBack={() => {
            trackEvent(
              PatientClinicalEvents[
                `${openOrderEquipScaleFrom}EnterAddressManuallyModalBackClicked`
              ],
              {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              },
            );
            setCurrentStep(OrderSteps.SHIPPING_ADDRESS);
          }}
          onUseAddress={(address) => {
            trackEvent(
              PatientClinicalEvents[
                `${openOrderEquipScaleFrom}EnterAddressManuallyModalUseThisAddressClicked`
              ],
              {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              },
            );
            handleAddressSelection(address);
          }}
          selectedAddress={selectedAddress}
        />
      </ChakraFlex>
    );
  };

  const ReviewOrderStep = ({ selectedAddress, setCurrentStep }) => (
    <ChakraFlex
      direction="column"
      gap="18px"
      id={`${TOP_LEVEL_ID}__review-order-step`}
    >
      <AddressCard
        {...selectedAddress}
        id={`${TOP_LEVEL_ID}__review-order-address-card`}
        phoneNumber={formatCellPhoneNumber(selectedAddress?.phoneNumber)}
        userRole={selectedAddress?.userRole}
      />
      {orderBodyTraceError && (
        <ToastMessage
          icon={FAIconName.exclamationTriangleRegular}
          id={`${TOP_LEVEL_ID}__warning`}
          isFullWidth
          text={"Sorry, we couldn't place your order. Please try again."}
          variant="warning"
        />
      )}

      <ChakraFlex justifyContent="space-between">
        <FireflyButton
          id={`${TOP_LEVEL_ID}__back-button`}
          leftIcon={FAIconName.chevronLeftRegular}
          onClick={() => {
            trackEvent(
              PatientClinicalEvents[
                `${openOrderEquipScaleFrom}ReviewAndConfirmOrderModalBackClicked`
              ],
              {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              },
            );
            if (selectedAddress?.userRole !== null) {
              setCurrentStep(OrderSteps.SHIPPING_ADDRESS);
            } else {
              setCurrentStep(OrderSteps.ENTER_MANUAL_ADDRESS);
            }
          }}
          variant="secondary"
        >
          Back
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm-order-button`}
          isLoading={orderBodyTraceLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={() => {
            trackEvent(
              PatientClinicalEvents[
                `${openOrderEquipScaleFrom}ReviewAndConfirmOrderModalConfirmOrderClicked`
              ],
              {
                source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
              },
            );
            handleConfirmOrder();
          }}
          variant="primary"
        >
          Confirm Order
        </FireflyButton>
      </ChakraFlex>
    </ChakraFlex>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case OrderSteps.WARNING:
        return (
          <WarningStep
            currentWarning={currentWarning}
            handleProceedWithOrder={handleProceedWithOrder}
            warnings={warnings}
          />
        );

      case OrderSteps.SHIPPING_ADDRESS:
        const isAddressDataLoaded = addressData?.length > 0;
        return !isAddressDataLoaded ? (
          <SkeletonLoader
            height="173px"
            id={`${TOP_LEVEL_ID}__skeleton-loader`}
            isLoaded={isAddressDataLoaded}
            width="500px"
          />
        ) : (
          <ShippingAddressStep
            AddressData={addressData}
            handleAddressSelection={handleAddressSelection}
          />
        );
      case OrderSteps.ENTER_MANUAL_ADDRESS:
        return (
          <EnterManualAddressStep
            addressData={addressData}
            handleAddressSelection={handleAddressSelection}
            setCurrentStep={setCurrentStep}
          />
        );

      case OrderSteps.REVIEW_ORDER:
        return (
          <ReviewOrderStep
            selectedAddress={selectedAddress}
            setCurrentStep={setCurrentStep}
          />
        );

      default:
        return null;
    }
  };
  return (
    <ModalV2
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={!!openOrderEquipScaleFrom}
      onClose={onClose}
      size="xl"
      title={modalTitle}
    >
      {renderStepContent()}
    </ModalV2>
  );
};

export default OrderEquipScale;
